"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.MAIN_ANALYTICS_CATEGORIES = exports.CURRENT_ANALYTICS_VERSION = void 0;
exports.getAnalyticsVersionDescription = getAnalyticsVersionDescription;
exports.getAnalyticsVersionsToDescriptions = getAnalyticsVersionsToDescriptions;
var _missionStateConstants = require("../constants/missionStateConstants");
var _versionDescriptionUpdaters = require("./versionDescriptionUpdaters");
const MAIN_ANALYTICS_CATEGORIES = exports.MAIN_ANALYTICS_CATEGORIES = {
  QUESTIONS: 'QUESTIONS',
  APPLICATION: 'APPLICATION',
  CRITICAL_THINKING: 'CRITICAL_THINKING',
  COLLABORATION: 'COLLABORATION',
  GRIT: 'GRIT',
  INITIATIVE: 'INITIATIVE'
};
const END_OF_MISSION_SURVEY_INTERNAL_METRIC_NAME = 'endOfMissionSurvey';
const END_OF_MISSION_SURVEY_DEPRECATION_REASON = 'survey was removed from the end of mission sequence.';
const END_OF_MISSION_SURVEY_METRIC = {
  metricTitle: 'Self Survey',
  generalDescription: "The student's self-perception of their abilities (from end of mission survey).",
  calculationDescription: '(self score from survey for metric) / (max possible score for metric on survey)',
  forMissionType: [_missionStateConstants.MISSION_TYPES.FOUR_PLUS, _missionStateConstants.MISSION_TYPES.K_THROUGH_3]
};

/**
 * getAnalyticsVersionsToDescriptions - get a map of analytics versions to their descriptions
 *
 * @return {{[versionNumber: string]: AnalyticsVersionDescription}} - a mapping of all known analytics versions to their descriptions
 */
function getAnalyticsVersionsToDescriptions() {
  // determine the version descriptions and populate the cache

  const ANALYTICS_VERSION_1 = {
    canCompareWithPreviousVersion: false,
    changesFromPreviousVersion: {
      public: [],
      internal: ['Original analytics version, there is no previous version that could have changed.']
    },
    descriptionOfCategories: {
      [MAIN_ANALYTICS_CATEGORIES.QUESTIONS]: {
        descriptionOfCategory: 'The Question Score can consist of:',
        metricCalculationDescriptions: {
          questions: {
            metricTitle: 'Questions',
            generalDescription: 'Performance on included curriculum questions. These are the questions you can edit when creating a mission.',
            calculationDescription: '(score of graded questions) / (maximum possible score of graded questions)',
            forMissionType: [_missionStateConstants.MISSION_TYPES.K_THROUGH_3, _missionStateConstants.MISSION_TYPES.FOUR_PLUS]
          }
        }
      },
      [MAIN_ANALYTICS_CATEGORIES.APPLICATION]: {
        descriptionOfCategory: 'The Application Score can consist of:',
        metricCalculationDescriptions: {
          application: {
            metricTitle: 'Application',
            generalDescription: 'Accuracy of decisions made during the mission.',
            calculationDescription: '(number of times culminating moment answered correctly) / (number of times culminating moment answered)',
            forMissionType: [_missionStateConstants.MISSION_TYPES.K_THROUGH_3, _missionStateConstants.MISSION_TYPES.FOUR_PLUS]
          }
        }
      },
      [MAIN_ANALYTICS_CATEGORIES.CRITICAL_THINKING]: {
        descriptionOfCategory: 'The Critical Thinking Score is a measure of how well information is utilized and prioritized. It can consist of:',
        metricCalculationDescriptions: {
          scannedData: {
            metricTitle: 'Vote on Data',
            generalDescription: "How well the student's votes on the data presented during the mission correlates with the relevancy of the data",
            calculationDescription: '(sum of the scaled difference between student voted relevancy and marked relevancy on each data card) / (total number of data cards presented)',
            forMissionType: [_missionStateConstants.MISSION_TYPES.FOUR_PLUS]
          },
          [END_OF_MISSION_SURVEY_INTERNAL_METRIC_NAME]: END_OF_MISSION_SURVEY_METRIC
        }
      },
      [MAIN_ANALYTICS_CATEGORIES.COLLABORATION]: {
        descriptionOfCategory: 'The Collaboration Score is a measure of how students effectively communicate and work together. It can consist of:',
        metricCalculationDescriptions: {
          socialHealthScore: {
            metricTitle: 'Social Health Score',
            generalDescription: "How well other students rate this student's interactions when performing health tasks.",
            calculationDescription: "(sum of student ratings for all of this student's interactions) / (max possible student rating for all of this student's interactions)",
            forMissionType: [_missionStateConstants.MISSION_TYPES.FOUR_PLUS]
          },
          engineerPanelScore: {
            metricTitle: 'Engineering Panel Time',
            generalDescription: 'How quickly the student completed their engineering panel tasks when on the ship repair station.',
            calculationDescription: '(sum scaled amount time to complete all tasks) / (total number of tasks assigned)',
            forMissionType: [_missionStateConstants.MISSION_TYPES.FOUR_PLUS]
          },
          [END_OF_MISSION_SURVEY_INTERNAL_METRIC_NAME]: END_OF_MISSION_SURVEY_METRIC
        }
      },
      [MAIN_ANALYTICS_CATEGORIES.GRIT]: {
        descriptionOfCategory: 'The Resilience Score is a measure of how well students persevere after negative moments. It can consist of:',
        metricCalculationDescriptions: {
          gritAfterNegativeEvents: {
            metricTitle: 'Resilience After Negative Events',
            generalDescription: 'How the student participated after mission failure or incorrectly answered questions.',
            calculationDescription: "average [per negative event] of ((the student's actions per minute after the negative event) / (the student's average actions per minute over the entire mission))",
            forMissionType: [_missionStateConstants.MISSION_TYPES.FOUR_PLUS, _missionStateConstants.MISSION_TYPES.K_THROUGH_3]
          }
        }
      },
      [MAIN_ANALYTICS_CATEGORIES.INITIATIVE]: {
        descriptionOfCategory: 'The Initiative Score is a measure of how well students respond to tasks on their own. It can consist of:',
        metricCalculationDescriptions: {
          alertResponse: {
            metricTitle: 'Alert Response Time',
            generalDescription: 'How quickly the student responded to alerts on the ship repair station.',
            calculationDescription: 'scaled (sum of the amount of time it took the student to start investigating alerts on the repair screen) / (total number of alerts that the student was assigned during the mission)',
            forMissionType: [_missionStateConstants.MISSION_TYPES.FOUR_PLUS]
          },
          scannedDataResponse: {
            metricTitle: 'Scanned Data Response Time',
            generalDescription: 'How quickly students voted on content cards after they become available.',
            calculationDescription: 'scaled (sum of the amount of time it took for students to vote on each content card available to them) / (total amount of contents cards available to the student throughout the mission)',
            forMissionType: [_missionStateConstants.MISSION_TYPES.FOUR_PLUS]
          },
          stationResponse: {
            metricTitle: 'Station Response Time',
            generalDescription: 'How quickly the student navigated to stations as they turned on when the student was not already on the station.',
            calculationDescription: 'scaled (sum of the amount of time it took for the student to navigate to a station after it turned on) / (total amount of times a station turned out throughout the mission)',
            forMissionType: [_missionStateConstants.MISSION_TYPES.FOUR_PLUS, _missionStateConstants.MISSION_TYPES.K_THROUGH_3]
          }
        }
      }
    }
  };
  const ANALYTICS_VERSION_2 = (0, _versionDescriptionUpdaters.applyAnalyticDefinitionUpdatersForNewVersion)(ANALYTICS_VERSION_1, false, [(0, _versionDescriptionUpdaters.addMetric)(MAIN_ANALYTICS_CATEGORIES.INITIATIVE, 'mapParticipation', {
    metricTitle: 'Map Participation Time',
    generalDescription: 'How often the student was scanning objects on a map when a map was available and there were objects left to scan.',
    calculationDescription: '(the total amount of time a student was scanning objects on maps) / (total amount of time possible students could have been scanning objects on maps)',
    forMissionType: [_missionStateConstants.MISSION_TYPES.FOUR_PLUS]
  }), (0, _versionDescriptionUpdaters.addMetric)(MAIN_ANALYTICS_CATEGORIES.INITIATIVE, 'boostParticipation', {
    metricTitle: 'Boost Time',
    generalDescription: 'How often the student had their station boosted throughout the mission.',
    calculationDescription: "(total amount of time the students station was boosted) / (total possible amount of time the student's station could be boosted)",
    forMissionType: [_missionStateConstants.MISSION_TYPES.FOUR_PLUS]
  })]);
  const ANALYTICS_VERSION_3 = (0, _versionDescriptionUpdaters.applyAnalyticDefinitionUpdatersForNewVersion)(ANALYTICS_VERSION_2, false, [
  // added metrics
  (0, _versionDescriptionUpdaters.addMetric)(MAIN_ANALYTICS_CATEGORIES.GRIT, END_OF_MISSION_SURVEY_INTERNAL_METRIC_NAME, END_OF_MISSION_SURVEY_METRIC), (0, _versionDescriptionUpdaters.addMetric)(MAIN_ANALYTICS_CATEGORIES.INITIATIVE, 'mapVotePercentage', {
    metricTitle: 'Map Vote Percentage',
    generalDescription: 'How many times the student voted on content cards.',
    calculationDescription: '(how many content cards the student voted on) / (total possible count of content cards the student could vote on)',
    forMissionType: [_missionStateConstants.MISSION_TYPES.FOUR_PLUS]
  }), (0, _versionDescriptionUpdaters.addMetric)(MAIN_ANALYTICS_CATEGORIES.INITIATIVE, 'creativeCanvas', {
    metricTitle: 'Creative Canvas Response Time',
    generalDescription: 'How quickly students started work on their creative canvases.',
    calculationDescription: 'scaled (sum of the amount of time it took for the student to start working on creative canvases after they became available) / (total count of creative canvases the student could participate on)',
    forMissionType: [_missionStateConstants.MISSION_TYPES.FOUR_PLUS]
  }), (0, _versionDescriptionUpdaters.addMetric)(MAIN_ANALYTICS_CATEGORIES.INITIATIVE, 'canvasDataResponseTime', {
    metricTitle: 'Canvas Data Response Time',
    generalDescription: 'How quickly students voted on the content cards after they became available during a creative canvas station.',
    calculationDescription: 'scaled (sum of the amount of time it took for the student to vote on content cards on the creative canvas station) / (total count of cards the student could vote on during all creative canvas stations throughout the mission)',
    forMissionType: [_missionStateConstants.MISSION_TYPES.FOUR_PLUS]
  }), (0, _versionDescriptionUpdaters.addMetric)(MAIN_ANALYTICS_CATEGORIES.INITIATIVE, 'creativeCanvasVoteParticipation', {
    metricTitle: 'Creative Canvas Vote Participation',
    generalDescription: 'How many times the student voted on content cards which were available on the creative canvas station.',
    calculationDescription: '(how many content cards the student voted on in the creative canvas station) / (total possible count of content cards the student could vote on in all creative canvas stations throughout the mission)',
    forMissionType: [_missionStateConstants.MISSION_TYPES.FOUR_PLUS]
  }), (0, _versionDescriptionUpdaters.addMetric)(MAIN_ANALYTICS_CATEGORIES.INITIATIVE, END_OF_MISSION_SURVEY_INTERNAL_METRIC_NAME, END_OF_MISSION_SURVEY_METRIC),
  // deprecatedMetrics
  (0, _versionDescriptionUpdaters.deprecateMetric)(MAIN_ANALYTICS_CATEGORIES.CRITICAL_THINKING, END_OF_MISSION_SURVEY_INTERNAL_METRIC_NAME, END_OF_MISSION_SURVEY_DEPRECATION_REASON), (0, _versionDescriptionUpdaters.deprecateMetric)(MAIN_ANALYTICS_CATEGORIES.COLLABORATION, END_OF_MISSION_SURVEY_INTERNAL_METRIC_NAME, END_OF_MISSION_SURVEY_DEPRECATION_REASON), (0, _versionDescriptionUpdaters.deprecateMetric)(MAIN_ANALYTICS_CATEGORIES.GRIT, END_OF_MISSION_SURVEY_INTERNAL_METRIC_NAME, END_OF_MISSION_SURVEY_DEPRECATION_REASON), (0, _versionDescriptionUpdaters.deprecateMetric)(MAIN_ANALYTICS_CATEGORIES.INITIATIVE, END_OF_MISSION_SURVEY_INTERNAL_METRIC_NAME, END_OF_MISSION_SURVEY_DEPRECATION_REASON), (0, _versionDescriptionUpdaters.deprecateMetric)(MAIN_ANALYTICS_CATEGORIES.INITIATIVE, 'boostParticipation', 'Boosting stations was removed from the stations (ie. boost no longer exists), the questions are now asked before a station starts.'), (0, _versionDescriptionUpdaters.deprecateMetric)(MAIN_ANALYTICS_CATEGORIES.INITIATIVE, 'scannedDataResponse', 'metric does not appear to be useful.'), (0, _versionDescriptionUpdaters.deprecateMetric)(MAIN_ANALYTICS_CATEGORIES.INITIATIVE, 'canvasDataResponseTime', 'metric is still in testing.' // this metric was added in this version, but the weight was also set to `0` (making it deprecated)
  )]);
  const ANALYTICS_VERSION_4 = (0, _versionDescriptionUpdaters.applyAnalyticDefinitionUpdatersForNewVersion)(ANALYTICS_VERSION_3, false, [(0, _versionDescriptionUpdaters.updateMetricCalculationDescription)(MAIN_ANALYTICS_CATEGORIES.GRIT, 'gritAfterNegativeEvents', '`scaled average [per negative event] of ((the student\'s actions per minute after the negative event) / (the student\'s average actions per minute over the entire mission))` "scaled" meaning that 50% equates to there being no change in actions per minute after a negative event compared to the entire mission.'), (0, _versionDescriptionUpdaters.removeMetric)(MAIN_ANALYTICS_CATEGORIES.INITIATIVE, 'boostParticipation', 'Boosting a station no longer exists in the controls.')]);
  const ANALYTICS_VERSION_5 = (0, _versionDescriptionUpdaters.applyAnalyticDefinitionUpdatersForNewVersion)(ANALYTICS_VERSION_4, true, [descriptor => {
    return {
      ...descriptor,
      changesFromPreviousVersion: {
        ...descriptor.changesFromPreviousVersion,
        public: [...descriptor.changesFromPreviousVersion.public, 'now every graded attempt for a creative canvas question is included instead of just the first attempt']
      }
    };
  }]);
  const analyticsVersionsToDescriptions = {
    '1': ANALYTICS_VERSION_1,
    '2': ANALYTICS_VERSION_2,
    '3': ANALYTICS_VERSION_3,
    '4': ANALYTICS_VERSION_4,
    '5': ANALYTICS_VERSION_5
  };
  return analyticsVersionsToDescriptions;
}

/**
 * getAnalyticsVersionDescription - get the description of the analytics of a specific version
 *
 * @param {number} version - the version number to get the description for
 *
 * @return {?{
 *  exactMatch: boolean, - true if the exact description of the version could be determined
 *	usingVersion: number, - the version of the analytics the description is for
 *	description: AnalyticsVersionDescription, - a description of how this version of the analytics changed and how its fields are calculated
 * }} - null/undefined if the analytics version description could nto be determined
 */
function getAnalyticsVersionDescription(version) {
  let exactMatch = true;
  let usingVersion = version;
  if (usingVersion > CURRENT_ANALYTICS_VERSION) {
    usingVersion = CURRENT_ANALYTICS_VERSION;
    exactMatch = false;
  }
  const description = getAnalyticsVersionsToDescriptions()[String(usingVersion)];
  if (!description) {
    return null;
  }
  return {
    exactMatch,
    usingVersion,
    description
  };
}
const CURRENT_ANALYTICS_VERSION = exports.CURRENT_ANALYTICS_VERSION = 5;